<template>
    <AppModal @close="onClose()" v-bind="$attrs" v-if="visible">
        <template v-slot:header>Add Building Rooms/Suites</template>
        <form @submit.prevent="onSubmit()">
            <transition name="fade">
                <AppNotification :type="alertMessage.type" v-if="alertMessage.message">
                    {{alertMessage.message}}
                </AppNotification>
            </transition>
            <div class="form-group">
                <label for="spaceType">Type</label>
                <select :class="{'is-invalid': $v.spaceType.$invalid && isValidating}"
                        class="form-control" v-model="spaceType">
                    <option :value="null" disabled>--Select Type--</option>
                    <option :key="type.id" :value="type.id" v-for="type in spaceTypes">{{type.name}}</option>
                </select>
                <span class="invalid-feedback" v-if="!$v.spaceType.required && isValidating">Field is required</span>
            </div>
            <b-form-checkbox v-if="spaceType !== null" v-model="addRange" name="check-button" switch class="pb-3">
                Set Range of {{ spaceTypeName }}s?
            </b-form-checkbox>
            <div v-if="!addRange && spaceType !== null" class="form-group">
                <label for="spaceIdentity">{{ spaceTypeName }} Number</label>
                <input :class="{'is-invalid': $v.spaceIdentity.$invalid && isValidating}"
                       class="form-control"
                        maxlength="30"
                        v-model="spaceIdentity"/>
                <span class="invalid-feedback" v-if="!$v.spaceIdentity.required && isValidating">Field is required</span>
                <span class="invalid-feedback" v-if="!$v.spaceIdentity.alphaNum && isValidating">Field cannot include special characters</span>
            </div>
            <div v-if="addRange && spaceType !== null" class="pb-3">
                <label for="spaceIdentity">Start {{ spaceTypeName }} Number</label>
                <input  :class="{'is-invalid': $v.spaceIdentityStart.$invalid && isValidating}"
                        class="form-control"
                        maxlength="30"
                        v-model="spaceIdentityStart"/>
                <span class="invalid-feedback" v-if="!$v.spaceIdentityStart.required && isValidating">Field is required</span>
                <span class="invalid-feedback" v-if="!$v.spaceIdentityStart.numeric && isValidating">Field cannot include letters</span>
            </div>
            <div v-if="addRange && spaceType !== null" class="pb-3">
                <label for="spaceIdentity">End {{ spaceTypeName }} Number</label>
                <input :class="{'is-invalid': $v.spaceIdentityEnd.$invalid && isValidating}"
                       class="form-control"
                        maxlength="30"
                        v-model="spaceIdentityEnd"/>
                <span class="invalid-feedback" v-if="!$v.spaceIdentityEnd.required && isValidating">Field is required</span>
                <span class="invalid-feedback" v-if="!$v.spaceIdentityEnd.numeric && isValidating">Field cannot include letters</span>
            </div>
            <b-form-checkbox v-model="hasParentBuildingSpace" name="check-button" switch class="pb-3">
                Include Parent Room/Suite
            </b-form-checkbox>
            <div v-if="hasParentBuildingSpace" class="form-group">
                <label for="parentBuildingSpaceId">Parent Room/Suite</label>
                <select class="form-control" v-model="parentBuildingSpaceId">
                    <option :value="null" disabled>--Select Parent--</option>
                    <option :key="parentBuildingSpace.id" :value="parentBuildingSpace.id" v-for="parentBuildingSpace in possibleParentBuildingSpaces">{{parentBuildingSpace.spaceType}} {{parentBuildingSpace.spaceIdentity}}</option>
                </select>
            </div>
            <b-form-checkbox v-if="spaceType !== null" v-model="shouldCreateFacilitiesLock" name="check-button" class="pb-3">
                Create Lock for New {{ spaceTypeName }}{{ addRange ? 's' : '' }}
            </b-form-checkbox>
        </form>
        <template v-slot:footer>
            <button :disabled="isSubmitting" @click.prevent="onSubmit()" class="btn btn-primary" type="submit">Save
            </button>
            <button :disabled="isSubmitting" @click="onClose()" class="btn btn-danger" data-dismiss="modal"
                    type="button">
                Close
            </button>
        </template>
    </AppModal>
</template>

<script>
    import AppModal from "../shared/AppModal";
    import AppNotification from "@/components/shared/AppNotification";
    import BuildingSpaceService from "@/services/BuildingSpaceService";
    import {required, requiredIf, numeric, alphaNum} from "vuelidate/lib/validators";
    import {ToastType} from "@/services/models/shared/ToastType";

    export default {
        components: {
            AppModal,
            AppNotification
        },
        data() {
            return {
                hasParentBuildingSpace: false,
                addRange: false,
                possibleParentBuildingSpaces: [],
                parentBuildingSpacesFilterOptions: {
                    returnAll: true,
                    page: 1,
                    pageSize: 10,
                    buildingId: this.buildingId,
                    spaceType: null,
                    parentSpaceType: null,
                    spaceIdentity: null,
                    excludeBuildingSpaceId: null
                },
                isSubmitting: false,
                isValidating: false,
                spaceTypes: [
                    {
                        id: 1,
                        name: 'Room'
                    },
                    {
                        id: 2,
                        name: 'Suite'
                    }
                ],
                alertMessage: {
                    type: null,
                    message: null
                },
                spaceType: null,
                parentBuildingSpaceId: null,
                spaceIdentityStart: null,
                spaceIdentityEnd: null,
                spaceIdentity: null,
                shouldCreateFacilitiesLock: false
            }
        },
        computed: {
            spaceTypeName () {
              const vm = this;
              const spaceTypeName = this.spaceTypes.find(e => { return e.id == vm.spaceType; });
              if (spaceTypeName) {
                return spaceTypeName.name;
              }
              return null;
            }
        },
        props: {
            buildingId: {
                type: String,
                required: true,
            },
            visible: {
                required: false,
                default: false
            }
        },
        watch: {
            visible() {
                this.possibleParentBuildingSpaces = [];
                this.hasParentBuildingSpace = false;
            },
            /**
             * @description Watches the hasParentBuildingSpace for check. If it is checked it gets the list of parent building spaces
             */
            hasParentBuildingSpace(newVal) {
                if (newVal) {
                  const vm = this;
                  BuildingSpaceService.getBuildingSpaces(this.buildingId, this.parentBuildingSpacesFilterOptions)
                    .then(response => {
                      vm.possibleParentBuildingSpaces = response.data;
                    }).catch(() => {
                        this.parentBuildingSpaceId = null;
                        vm.possibleParentBuildingSpaces = [];
                        vm.alertMessage.type = "danger";
                        vm.alertMessage.message = "Unable to load list of parent rooms/suites for this building.";
                    });
                    return;
                }
                this.parentBuildingSpaceId = null;
                this.possibleParentBuildingSpaces = [];
            },

            addRange(newVal) {
              if (!newVal) {
                this.spaceIdentityEnd = null;
                this.spaceIdentityStart = null;
              }
              else {
                this.spaceIdentity = null;
              }
            }
        },
        methods: {
            /**
             * @description Emits the close event to the parent
             */
            onClose() {
                this.hasParentBuildingSpace = false;
                this.parentBuildingSpaceId = null;
                this.possibleParentBuildingSpaces = [];
                this.spaceType = null;
                this.spaceIdentity = null;
                this.spaceIdentityStart = null;
                this.spaceIdentityEnd = null;
                this.addRange = false;
                this.shouldCreateFacilitiesLock = false;
                this.isValidating = false;
                this.isSubmitting = false;
                this.alertMessage = {
                    type: null,
                    message: null
                }
                this.$emit('close')
            },

            /**
             * @description Creates a new list of rooms/suites and emits the event to the parent
             */
            onSubmit() {
                const vm = this;
                vm.isValidating = true;

                // stop here if form is invalid
                this.$v.$touch();
                if (this.$v.$invalid) {
                    return;
                }

                vm.isSubmitting = true;
                const buildingSpaceList = this.createBuildingSpaceList();
                BuildingSpaceService.addBuildingSpaces(this.buildingId, buildingSpaceList)
                    .then(() => {
                      vm.alert(ToastType.Success, "The rooms/suites were successfully created.");

                      vm.$emit('create');
                      vm.onClose();
                    })
                    .catch((error) => {
                      vm.alert(ToastType.Danger, error.response.data)
                    })
                    .finally(() => {
                      vm.onClose();
                    })
            },

            createBuildingSpaceList() {
              if (this.addRange) {
                if (this.spaceIdentityStart != null) {
                  if (this.spaceIdentityEnd != null) {
                    const buildingSpaceRange = [];
                    for (let i = this.spaceIdentityStart; i <= this.spaceIdentityEnd; i++) {
                      buildingSpaceRange.push({
                        spaceType: this.spaceType,
                        parentBuildingSpaceId: this.parentBuildingSpaceId,
                        spaceIdentity: i,
                        shouldCreateFacilitiesLock: this.shouldCreateFacilitiesLock,
                        createdByPersonId: this.$store.getters.user.id
                      })
                    }
                    return buildingSpaceRange;
                  }
                  return {
                    spaceType: this.spaceType,
                    parentBuildingSpaceId: this.parentBuildingSpaceId,
                    spaceIdentity: this.spaceIdentityStart,
                    shouldCreateFacilitiesLock: this.shouldCreateFacilitiesLock,
                    createdByPersonId: this.$store.getters.user.id
                  }
                }
              }
              return [{
                spaceType: this.spaceType,
                parentBuildingSpaceId: this.parentBuildingSpaceId,
                spaceIdentity: this.spaceIdentity,
                shouldCreateFacilitiesLock: this.shouldCreateFacilitiesLock,
                createdByPersonId: this.$store.getters.user.id
              }]
            }
        },
        validations: {
            spaceType: {
                required
            },
            spaceIdentity: {
                required: requiredIf(function () {
                    return !this.addRange;
                }),
              alphaNum
            },
            spaceIdentityStart: {
                required: requiredIf(function () {
                  return this.addRange;
                }),
                numeric
            },
            spaceIdentityEnd: {
                required: requiredIf(function () {
                  return this.addRange;
                }),
                numeric
            }
        }
    }
</script>
